import { Fragment, type ReactNode } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDown, MoreVertical, type LucideIcon } from 'lucide-react';

import { cn } from '../../lib/cn';

/**
 * MenuDropdown — Agent Builder's "Menu" pattern (WorkflowManager.jsx):
 * single button (MoreVertical icon + "Menu" + ChevronDown) opens a panel
 * with sectioned items. Each section has a small uppercase label and any
 * number of clickable rows.
 *
 *   Appearance         <-- section label (text-[10px] uppercase tracking)
 *     Switch to Light
 *   ─────
 *   Language
 *     English
 *     Español
 *   ─────
 *   Legal
 *     License
 *     About IGNODE
 *
 * Uses HeadlessUI Menu primitive so keyboard nav + focus trap + outside
 * click + ESC are handled correctly.
 */
export interface MenuItem {
    key: string;
    icon?: LucideIcon;
    /** Tailwind text-color class for the icon (e.g. "text-amber-500").
     *  Mirrors the Agent Builder palette where each menu item icon gets
     *  a category-specific tint instead of the default muted gray. */
    iconClassName?: string;
    label: string;
    onClick: () => void;
    /** Mark as currently-active (e.g. selected language). */
    selected?: boolean;
    /** Render greyed-out + non-clickable + optional tooltip. Useful for
     *  surfacing actions that exist but require a precondition (e.g.
     *  Export only works when the project is locked). */
    disabled?: boolean;
    /** title attribute — usually paired with `disabled` to tell the user
     *  what needs to be true for the item to become clickable. */
    title?: string;
}

export interface MenuSection {
    label: string;
    items: MenuItem[];
}

interface MenuDropdownProps {
    isDarkMode?: boolean;
    sections: MenuSection[];
    /** Label shown next to MoreVertical icon. Defaults to "Menu". */
    triggerLabel?: ReactNode;
    /** Override the entire trigger button — `triggerLabel` ignored when set. */
    trigger?: ReactNode;
    className?: string;
}

export default function MenuDropdown({
    isDarkMode = false,
    sections,
    triggerLabel = 'Menu',
    trigger,
    className,
}: MenuDropdownProps) {
    const panelBg = isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
    const sectionLabelCls = isDarkMode ? 'text-gray-500' : 'text-gray-400';
    const dividerCls = isDarkMode ? 'border-gray-700' : 'border-gray-200';
    const itemBaseCls = isDarkMode ? 'text-gray-300' : 'text-gray-700';
    const itemHoverCls = isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-50';
    const activeRingCls = isDarkMode ? 'bg-gray-800' : 'bg-gray-100';

    return (
        <Menu as="div" className={cn('relative', className)}>
            {({ open }) => (
                <>
                    {/* When a custom `trigger` is supplied, render
                        Menu.Button as a bare span (`as={Fragment}` is
                        not viable — HeadlessUI needs an element to
                        attach the click handler to). The span has no
                        styling of its own; the caller's trigger
                        controls the entire appearance.

                        When no custom trigger is given, fall back to
                        the standard "Menu" pill button styling for
                        backwards compat with existing call sites. */}
                    {trigger ? (
                        <Menu.Button as="span" className="inline-flex">
                            {trigger}
                        </Menu.Button>
                    ) : (
                        <Menu.Button
                            className={cn(
                                'px-4 py-2 rounded-lg text-[13px] font-semibold flex items-center gap-2 border transition-colors duration-150',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                                open
                                    ? isDarkMode
                                        ? 'bg-gray-700 border-gray-600 text-white'
                                        : 'bg-gray-100 border-gray-400 text-gray-900'
                                    : isDarkMode
                                        ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
                            )}
                        >
                            <MoreVertical size={16} />
                            <span>{triggerLabel}</span>
                            <ChevronDown size={14} />
                        </Menu.Button>
                    )}
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <Menu.Items
                            className={cn(
                                'absolute right-0 mt-2 w-56 rounded-lg border shadow-xl z-50 overflow-hidden py-1',
                                'focus-visible:outline-none',
                                panelBg,
                            )}
                        >
                            {sections.map((section, idx) => (
                                <Fragment key={section.label}>
                                    {idx > 0 && <div className={cn('my-1 border-t', dividerCls)} />}
                                    <div
                                        className={cn(
                                            'px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider',
                                            sectionLabelCls,
                                        )}
                                    >
                                        {section.label}
                                    </div>
                                    {section.items.map((item) => {
                                        const Icon = item.icon;
                                        return (
                                            <Menu.Item key={item.key} disabled={item.disabled}>
                                                {({ active }) => (
                                                    <button
                                                        type="button"
                                                        onClick={item.disabled ? undefined : item.onClick}
                                                        disabled={item.disabled}
                                                        title={item.title}
                                                        className={cn(
                                                            'w-full text-left px-3 py-2 text-[13px] flex items-center gap-2 transition-colors duration-100',
                                                            itemBaseCls,
                                                            (active || item.selected) && !item.disabled && itemHoverCls,
                                                            item.selected && activeRingCls,
                                                            item.disabled && 'opacity-50 cursor-not-allowed',
                                                        )}
                                                    >
                                                        {Icon ? (
                                                            <Icon
                                                                size={14}
                                                                className={cn('shrink-0', item.iconClassName)}
                                                            />
                                                        ) : (
                                                            <span className="w-3.5" />
                                                        )}
                                                        <span className={cn('flex-1', item.selected && 'font-semibold')}>{item.label}</span>
                                                    </button>
                                                )}
                                            </Menu.Item>
                                        );
                                    })}
                                </Fragment>
                            ))}
                        </Menu.Items>
                    </Transition>
                </>
            )}
        </Menu>
    );
}
